import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, InputGroup, FormControl } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Navbarpro from './sidebar';
import { api } from '../../../config';

function Vendor() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    businessName: '',
    ownerName: '',
    email: '',
    phone: '',
    address: '',
    category: 'Technical',
    subCategory: '',
    taxId: '',
    password: '',
    confirmPassword: '',
    latitude: '',
    longitude: '',
    documentUrl: ''
  });
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const subCategories = {
    'Technical': [
      'Architects', 'Civil Engineer', 'Site Supervisor', 'Survey Engineer',
      'MEP Consultant', 'Structural Engineer', 'Project Manager', 'HVAC Engineer',
      'Safety Engineer', 'Contractor', 'Interior Designer', 'Waterproofing Consultant', 'Acoustic Consultants'
    ],
    'Non-Technical': [
      'Earthwork Labour', 'Civil Mason', 'Shuttering/Centring Labour', 'Plumber',
      'Electrician', 'Painter', 'Carpenter', 'Flooring Labour', 'False Ceiling Worker'
    ],
    'Products': [
      'Building Materials', 'Electrical Supplies', 'Plumbing Supplies', 'Hardware', 'Safety Equipment'
    ]
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    if (name === 'category') {
      setForm(prev => ({ ...prev, subCategory: '' }));
    }
  };

  const handleGetLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setForm(prev => ({ ...prev, latitude: lat, longitude: lng }));

          try {
            const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
            const data = await res.json();
            if (data.display_name) {
              setForm(prev => ({ ...prev, address: data.display_name }));
            }
          } catch (err) {
            console.log('Address lookup failed');
          }
        },
        (err) => {
          setError('Location access denied. Please enter address manually.');
        }
      );
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'apna_mestri');

    try {
      const res = await fetch('https://api.cloudinary.com/v1_1/dofpzxlvc/auto/upload', {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      setForm(prev => ({ ...prev, documentUrl: data.secure_url }));
      setSuccess('Document uploaded successfully');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post('/add_vendor', {
        Business_Name: form.businessName,
        Owner_name: form.ownerName,
        Email_address: form.email,
        Phone_number: form.phone,
        Business_address: form.address,
        Category: form.category,
        Sub_Category: form.subCategory ? [form.subCategory] : [],
        Tax_ID: form.taxId,
        Password: form.password,
        Latitude: form.latitude,
        Longitude: form.longitude,
        ProductUrl: form.documentUrl
      });

      setSuccess('Vendor added successfully!');
      setForm({
        businessName: '', ownerName: '', email: '', phone: '', address: '',
        category: 'Technical', subCategory: '', taxId: '', password: '',
        confirmPassword: '', latitude: '', longitude: '', documentUrl: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add vendor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Navbarpro />

      <Container fluid className="py-4 px-4">
        {/* Page Header */}
        <div className="page-header d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="mb-1">
              <i className="bi bi-plus-circle me-2" style={{ color: 'var(--primary)' }}></i>
              Add New Vendor
            </h4>
            <p className="text-secondary mb-0">Register a new vendor to the platform</p>
          </div>
          <Link to="/pending" className="btn btn-outline-warning btn-sm">
            <i className="bi bi-clock me-1"></i> View Pending
          </Link>
        </div>

        {/* Alerts */}
        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <form onSubmit={handleSubmit}>
          <Row className="g-4">
            {/* Business Information */}
            <Col lg={8}>
              <Card className="mb-4">
                <Card.Header>
                  <i className="bi bi-building me-2" style={{ color: 'var(--primary)' }}></i>
                  Business Information
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Business Name *</Form.Label>
                        <Form.Control
                          name="businessName"
                          value={form.businessName}
                          onChange={handleChange}
                          placeholder="Enter business name"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Owner Name *</Form.Label>
                        <Form.Control
                          name="ownerName"
                          value={form.ownerName}
                          onChange={handleChange}
                          placeholder="Enter owner name"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Email Address *</Form.Label>
                        <InputGroup>
                          <InputGroup.Text><i className="bi bi-envelope"></i></InputGroup.Text>
                          <Form.Control
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder="email@example.com"
                            required
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Phone Number *</Form.Label>
                        <InputGroup>
                          <InputGroup.Text><i className="bi bi-phone"></i></InputGroup.Text>
                          <Form.Control
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                            placeholder="9876543210"
                            required
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Business Address *</Form.Label>
                        <InputGroup>
                          <Form.Control
                            name="address"
                            value={form.address}
                            onChange={handleChange}
                            placeholder="Enter complete address"
                            required
                          />
                          <Button variant="outline-primary" onClick={handleGetLocation}>
                            <i className="bi bi-geo-alt me-1"></i> Auto-detect
                          </Button>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Tax ID / GST</Form.Label>
                        <Form.Control
                          name="taxId"
                          value={form.taxId}
                          onChange={handleChange}
                          placeholder="Enter Tax ID or GST number"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Category Selection */}
              <Card className="mb-4">
                <Card.Header>
                  <i className="bi bi-tags me-2" style={{ color: 'var(--accent-cyan)' }}></i>
                  Category & Specialization
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Category *</Form.Label>
                        <Form.Select
                          name="category"
                          value={form.category}
                          onChange={handleChange}
                          required
                        >
                          <option value="Technical">Technical</option>
                          <option value="Non-Technical">Non-Technical</option>
                          <option value="Products">Products</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Sub-Category *</Form.Label>
                        <Form.Select
                          name="subCategory"
                          value={form.subCategory}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select Sub-Category</option>
                          {subCategories[form.category]?.map((sub, i) => (
                            <option key={i} value={sub}>{sub}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Security */}
              <Card>
                <Card.Header>
                  <i className="bi bi-shield-lock me-2" style={{ color: 'var(--accent-green)' }}></i>
                  Account Security
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Password *</Form.Label>
                        <InputGroup>
                          <InputGroup.Text><i className="bi bi-lock"></i></InputGroup.Text>
                          <Form.Control
                            type="password"
                            name="password"
                            value={form.password}
                            onChange={handleChange}
                            placeholder="Minimum 8 characters"
                            required
                            minLength={8}
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Confirm Password *</Form.Label>
                        <InputGroup>
                          <InputGroup.Text><i className="bi bi-lock-fill"></i></InputGroup.Text>
                          <Form.Control
                            type="password"
                            name="confirmPassword"
                            value={form.confirmPassword}
                            onChange={handleChange}
                            placeholder="Confirm password"
                            required
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            {/* Sidebar */}
            <Col lg={4}>
              {/* Document Upload */}
              <Card className="mb-4">
                <Card.Header>
                  <i className="bi bi-file-earmark-text me-2" style={{ color: 'var(--accent-purple)' }}></i>
                  Documents
                </Card.Header>
                <Card.Body>
                  <div
                    style={{
                      border: '2px dashed var(--border-color)',
                      borderRadius: '12px',
                      padding: '2rem',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => document.getElementById('docUpload').click()}
                  >
                    {uploadingDoc ? (
                      <Spinner size="sm" />
                    ) : form.documentUrl ? (
                      <>
                        <i className="bi bi-check-circle fs-2 text-success mb-2 d-block"></i>
                        <span className="text-success">Document uploaded</span>
                      </>
                    ) : (
                      <>
                        <i className="bi bi-cloud-upload fs-2 text-muted mb-2 d-block"></i>
                        <span className="text-secondary">Click to upload</span>
                        <br />
                        <small className="text-muted">PDF, JPG, PNG (Max 5MB)</small>
                      </>
                    )}
                    <input
                      type="file"
                      id="docUpload"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleDocumentUpload}
                      hidden
                    />
                  </div>
                  {form.documentUrl && (
                    <a href={form.documentUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm w-100 mt-3">
                      <i className="bi bi-eye me-1"></i> View Document
                    </a>
                  )}
                </Card.Body>
              </Card>

              {/* Location */}
              <Card className="mb-4">
                <Card.Header>
                  <i className="bi bi-geo-alt me-2" style={{ color: 'var(--accent-red)' }}></i>
                  Location Coordinates
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label className="small text-secondary">Latitude</Form.Label>
                        <Form.Control
                          size="sm"
                          name="latitude"
                          value={form.latitude}
                          onChange={handleChange}
                          placeholder="Auto-detected"
                          readOnly
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label className="small text-secondary">Longitude</Form.Label>
                        <Form.Control
                          size="sm"
                          name="longitude"
                          value={form.longitude}
                          onChange={handleChange}
                          placeholder="Auto-detected"
                          readOnly
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  {!form.latitude && (
                    <Button variant="outline-secondary" size="sm" className="w-100 mt-3" onClick={handleGetLocation}>
                      <i className="bi bi-crosshair me-1"></i> Get Current Location
                    </Button>
                  )}
                </Card.Body>
              </Card>

              {/* Submit */}
              <div className="d-grid gap-2">
                <Button type="submit" variant="primary" size="lg" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Adding Vendor...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-plus-circle me-2"></i>
                      Add Vendor
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={() => setForm({
                    businessName: '', ownerName: '', email: '', phone: '', address: '',
                    category: 'Technical', subCategory: '', taxId: '', password: '',
                    confirmPassword: '', latitude: '', longitude: '', documentUrl: ''
                  })}
                >
                  <i className="bi bi-x-circle me-1"></i> Clear Form
                </Button>
              </div>
            </Col>
          </Row>
        </form>
      </Container>
    </div>
  );
}

export default Vendor;
